import { getAccountDebt } from 'admin/actions/account';
import { components } from 'admin/schema/docs/api/admin';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Grid from '../../../atoms/Grid';
import Headings from '../../../atoms/Headings';
import Link from '../../../atoms/Link';

type Customer = components['schemas']['Customer'];
type PrivateMoney = components['schemas']['PrivateMoney'];

const FormGrid = styled(Grid.Container)`
  margin: 16px;
`;

const SectionHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 2px solid black;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionDetail = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #cccccc;
`;

const Chevron = styled.span`
  position: relative;
  top: 1px;
  font-size: 24px;
  font-weight: 200;
`;

const SectionDetailValue = styled.div``;

const UserStatus = ({
  customer,
  selectedPrivateMoney,
}: {
  customer: Customer;
  selectedPrivateMoney?: PrivateMoney;
}) => {
  const [hasDebt, setHasDebt] = useState(false);

  useEffect(() => {
    if (customer.account_uuid) {
      getAccountDebt(customer.account_uuid).then((res) => setHasDebt(res.count > 0));
    }
  }, [customer.account_uuid]);

  const {
    total: { money_amount: moneyAmount = 0, point_amount: pointAmount = 0 } = {},
    summary: {
      topup: {
        count: topupCount = 0,
        money: topupMoney = 0,
        raw_point: topupRawPoint = 0,
        campaign_point: topupCampaignPoint = 0,
      } = {},
      payment: {
        count: paymentCount = 0,
        money: paymentMoney = 0,
        raw_point: paymentRawPoint = 0,
        campaign_point: paymentCampaignPoint = 0,
      } = {},
    } = {},
  } = customer;

  return (
    <FormGrid columnGap={30}>
      <Grid.Item size={3} flexDirection="column" gap="4px">
        <SectionHeadingWrapper>
          <Headings.h5>マネー残高</Headings.h5>
          <Headings.h6>{`(${moment().format('yyyy/MM/DD')}時点)`}</Headings.h6>
        </SectionHeadingWrapper>
        <SectionBody>
          <Headings.h5>{moneyAmount.toLocaleString()}</Headings.h5>
          <Link
            to={{
              pathname: `/customers/${customer.id}/balance`,
              state: { selectedPrivateMoney },
            }}
          >
            有効期限を見る <Chevron>&rsaquo;</Chevron>
          </Link>
        </SectionBody>
      </Grid.Item>

      <Grid.Item size={3} flexDirection="column" gap="4px">
        <SectionHeadingWrapper>
          <Headings.h5>ポイント残高</Headings.h5>
          <Headings.h6>{`(${moment().format('yyyy/MM/DD')}時点)`}</Headings.h6>
        </SectionHeadingWrapper>
        <SectionBody>
          <Headings.h5>{pointAmount.toLocaleString()}</Headings.h5>
          {hasDebt && (
            <Link
              to={{
                pathname: `/customers/${customer.id}/debt`,
                state: { customer },
              }}
            >
              負債情報を見る <Chevron>&rsaquo;</Chevron>
            </Link>
          )}
        </SectionBody>
      </Grid.Item>

      <Grid.Item size={3} flexDirection="column" gap="4px">
        <SectionHeadingWrapper>
          <Headings.h5>チャージ件数/総額</Headings.h5>
        </SectionHeadingWrapper>
        <SectionBody>
          <Headings.h5>{`${topupCount}件/${(
            topupMoney + topupRawPoint
          ).toLocaleString()}`}</Headings.h5>
          <SectionDetail>
            <SectionDetailValue>マネー</SectionDetailValue>
            <SectionDetailValue>{topupMoney.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
          <SectionDetail>
            <SectionDetailValue>ポイント</SectionDetailValue>
            <SectionDetailValue>{topupRawPoint.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
          <SectionDetail>
            <SectionDetailValue>ポイント（キャンペーン付与分）</SectionDetailValue>
            <SectionDetailValue>{topupCampaignPoint.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
        </SectionBody>
      </Grid.Item>

      <Grid.Item size={3} flexDirection="column" gap="4px">
        <SectionHeadingWrapper>
          <Headings.h5>支払い件数/総額</Headings.h5>
        </SectionHeadingWrapper>
        <SectionBody>
          <Headings.h5>{`${paymentCount}件/${(
            paymentMoney + paymentRawPoint
          ).toLocaleString()}`}</Headings.h5>
          <SectionDetail>
            <SectionDetailValue>マネー</SectionDetailValue>
            <SectionDetailValue>{paymentMoney.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
          <SectionDetail>
            <SectionDetailValue>ポイント</SectionDetailValue>
            <SectionDetailValue>{paymentRawPoint.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
          <SectionDetail>
            <SectionDetailValue>ポイント（キャンペーン付与分）</SectionDetailValue>
            <SectionDetailValue>{paymentCampaignPoint.toLocaleString()}</SectionDetailValue>
          </SectionDetail>
        </SectionBody>
      </Grid.Item>
    </FormGrid>
  );
};

export default UserStatus;
